'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import QuoteListTable from '../../../../../src/components/Quote/QuoteListTable';
import { SectionTitle } from '@benzinga/core-ui';
import { Ticker, TickerLink } from '@benzinga/ticker-ui';

import type { QuoteFund, BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import ETFHoldingsSummaryBoxes from '../../../../../src/components/Quote/ETFHoldingsSummaryBoxes';
import ETFFAQ from '../../../../../src/components/Quote/FAQ/ETFFAQ';
import DrilldownFooter from '../../../../../src/components/Quote/DrilldownFooter';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';

interface HoldingsContentProps extends BaseQuotePageProps {
  etfFund: QuoteFund | null;
}

interface Holding {
  symbol: string;
  name: string;
  sharepercentage: number;
}

const HoldingsColumnsDef = [
  {
    cellRenderer: ({ data }: { data: Holding }) => {
      return (
        <Ticker symbol={data.symbol} targetElement={<TickerLink symbol={data.symbol}>{data.symbol}</TickerLink>} />
      );
    },
    colSpan: 1,
    field: 'symbol',
    headerName: 'Ticker',
  },
  {
    colSpan: 2,
    field: 'name',
    headerName: 'Name',
    valueFormatter: ({ data }: { data: Holding }) => {
      return data.name || '—';
    },
  },
  {
    cellClass: 'text-right text-gray-700',
    colSpan: 1,
    field: 'sharepercentage',
    headerClass: 'text-right',
    headerName: 'Share %',
    valueFormatter: ({ data }: { data: Holding }) => {
      return Number(data.sharepercentage) ? `${Number(data.sharepercentage).toFixed(2)}%` : '—';
    },
  },
];

const HoldingsContent: React.FC<HoldingsContentProps> = ({ etfFund, metaProps, profile, symbol }) => {
  const { t } = useTranslation('quote');

  const fundHolders = etfFund?.fund_top_holders || [];

  return (
    <>
      <div className="main-div flex flex-col gap-y-8">
        {etfFund && <ETFHoldingsSummaryBoxes etfFund={etfFund} />}
        <div className="flex flex-col gap-2">
          <SectionTitle level={1} uppercase={false}>
            {metaProps.title}
          </SectionTitle>
          <p className="section-description">{metaProps.description}</p>
        </div>
        <QuoteListTable columnsDef={HoldingsColumnsDef} rowData={fundHolders} />
        <DrilldownFooter
          isETF={true}
          leftSection={
            <>
              <SectionTitle level={3} size="2xl">
                {t('Quote.FAQ.faq')}
              </SectionTitle>
              <ETFFAQ etfFund={etfFund} profile={profile} />
            </>
          }
          peers={profile.peers}
          peerSymbols={profile.peerSymbols}
          symbol={symbol}
          type={profile.schedule?.type ?? null}
        />
        <QuoteFooter activeTab="holdings" showBrowseLink={false} />
      </div>
    </>
  );
};

export default HoldingsContent;
